
import { Card, CardContent } from "@/components/ui/card";
import { Package } from "lucide-react";
import { Link } from "react-router-dom";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <Package className="h-8 w-8 text-purple-600" />
            <span className="text-2xl font-bold text-gray-900">OrderEaseOnline</span>
          </Link>
          <Link to="/" className="text-purple-600 hover:text-purple-700">
            Back to Home
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
              <p className="text-gray-600 mb-8">Last updated: January 1, 2024</p>

              {/* Etsy Attribution Notice */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
                <p className="text-orange-800 font-medium">
                  The term 'Etsy' is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.
                </p>
              </div>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="mb-4">We collect information you provide directly to us, such as when you:</p>
                    <ul className="list-disc list-inside mb-4 space-y-2">
                      <li>Create an account or sign up for our services</li>
                      <li>Connect your Etsy store to our platform</li>
                      <li>Use our order management features</li>
                      <li>Contact us for support</li>
                      <li>Subscribe to our newsletter or marketing communications</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Etsy Integration</h2>
                  <p className="text-gray-700 mb-4">
                    When you connect your Etsy store to our platform, we access your Etsy data through the Etsy API 
                    to provide order management and analytics services. We only access the data you explicitly authorize 
                    during the connection process. The term 'Etsy' is a trademark of Etsy, Inc. This application uses 
                    the Etsy API but is not endorsed or certified by Etsy, Inc.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="mb-4">We use the information we collect to:</p>
                    <ul className="list-disc list-inside mb-4 space-y-2">
                      <li>Provide, maintain, and improve our services</li>
                      <li>Process transactions and send related information</li>
                      <li>Send technical notices, updates, and support messages</li>
                      <li>Respond to your comments, questions, and requests</li>
                      <li>Monitor and analyze trends and usage patterns</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Payment and Billing Information</h2>
                  <p className="text-gray-700 mb-4">
                    When you subscribe to our services, we collect billing information necessary to process your payments. 
                    Payment processing is handled by secure third-party providers. Please note that all subscription fees 
                    are non-refundable, and subscription cancellations result in immediate termination of access to premium features. 
                    For complete details, please refer to our Terms of Service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Sharing</h2>
                  <p className="text-gray-700 mb-4">
                    We do not sell, trade, or otherwise transfer your personal information to third parties 
                    without your consent, except as described in this policy. We may share your information with:
                  </p>
                  <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">
                    <li>Service providers who assist us in operating our platform</li>
                    <li>Professional advisors such as lawyers and accountants</li>
                    <li>Authorities when required by law or to protect our rights</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
                  <p className="text-gray-700 mb-4">
                    We implement appropriate technical and organizational measures to protect your personal 
                    information against unauthorized access, alteration, disclosure, or destruction. However, 
                    no method of transmission over the internet is 100% secure.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
                  <p className="text-gray-700 mb-4">You have the right to:</p>
                  <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">
                    <li>Access and update your personal information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Object to processing of your personal information</li>
                    <li>Request transfer of your personal information</li>
                    <li>Withdraw consent at any time</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                  <p className="text-gray-700">
                    If you have any questions about this Privacy Policy, please contact us at{" "}
                    <a href="mailto:support@ordereaseonline.com" className="text-purple-600 hover:underline">
                      support@ordereaseonline.com
                    </a>
                  </p>
                </section>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
